import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Meta (Facebook) Pixel route tracker.
 *
 * The pixel itself is initialized in index.html (id 372230267810091).
 * Because this app is a SPA, PageView must be fired manually on every
 * route change — otherwise only the very first page load is measured.
 *
 * Internal operator surfaces (/admin, /kiosk) are excluded on purpose so
 * back-office work and the physical kiosk device do not pollute ad data.
 */

export const META_PIXEL_ID = "372230267810091";

const EXCLUDED_PREFIXES = ["/admin", "/kiosk"];

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

const isExcluded = (pathname: string): boolean =>
  EXCLUDED_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

export default function MetaPixel() {
  const location = useLocation();
  const lastTrackedRef = useRef<string | null>(null);

  useEffect(() => {
    if (isExcluded(location.pathname)) return;

    const currentPath = `${location.pathname}${location.search}`;
    if (lastTrackedRef.current === currentPath) return;

    const fbq = window.fbq;
    if (typeof fbq !== "function") return;

    lastTrackedRef.current = currentPath;

    try {
      fbq("track", "PageView");
    } catch {
      // Never let analytics break navigation.
    }
  }, [location.pathname, location.search]);

  return null;
}